import React, { useState, useEffect } from 'react';
import { CloudSun, Clock, Calendar as CalendarIcon, Settings } from 'lucide-react';
import { useOutletContext, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import SchoolAgenda from './SchoolAgenda';
import { useT } from '../i18n.jsx';

const DashboardHome = () => {
  const { t, locale } = useT();
  const [time, setTime] = useState(new Date());
  const [weather, setWeather] = useState({ temp: '--', desc: t('dashboard.weatherLoading') });

  const { activeSchoolId, schoolMetadata, isMobile, user } = useOutletContext();

  const [calendarUrl, setCalendarUrl] = useState('');
  const [hideEmbed, setHideEmbed] = useState(false);


  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (schoolMetadata) {
      // Read as a positive: the field is absent for every school that configured an embed before
      // the switch existed, and treating absent as "hidden" would silently remove it from them.
      setHideEmbed(!!schoolMetadata.dashboardConfig?.hideEmbeddedCalendar);
      if (schoolMetadata.dashboardConfig?.calendarUrl) {
        setCalendarUrl(schoolMetadata.dashboardConfig.calendarUrl);
      } else {
        setCalendarUrl('');
      }
      const lat = schoolMetadata.branding?.latitude || 22.3193;
      const lon = schoolMetadata.branding?.longitude || 114.1694;
      const locName = schoolMetadata.branding?.locationName || 'Hong Kong';
      fetchWeather(lat, lon, locName);
    } else {
      fetchWeather(22.3193, 114.1694, 'Hong Kong');
    }
  }, [schoolMetadata]);

  const fetchWeather = async (lat, lon, locName) => {
    try {
      const response = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`);
      const data = await response.json();
      if (data.current_weather) {
        setWeather({
          temp: data.current_weather.temperature,
          desc: locName
        });
      }
    } catch (e) {
      setWeather({ temp: '--', desc: t('dashboard.weatherUnavailable') });
    }
  };

  const optionsTime = { timeZone: 'Asia/Hong_Kong', hour: '2-digit', minute: '2-digit', second: '2-digit' };
  const optionsDate = { timeZone: 'Asia/Hong_Kong', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.75rem' : '1.5rem', height: '100%' }}>

      {/* Top Info Card */}
      <div style={{
        backgroundColor: 'var(--color-primary)',
        color: 'white',
        borderRadius: isMobile ? 0 : 'var(--radius-lg)',
        padding: isMobile ? '1rem' : '1.5rem 2rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem',
        boxShadow: isMobile ? 'none' : 'var(--shadow-md)'
      }}>
        <div>
          <div style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: '700' }}>
            {time.toLocaleTimeString(locale, optionsTime)}
          </div>
          <div style={{ opacity: 0.8, fontSize: '0.875rem' }}>
            {time.toLocaleDateString(locale, optionsDate)}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: isMobile ? '1rem' : '1.25rem' }}>
          <CloudSun size={isMobile ? 24 : 32} />
          <span>{weather.temp}°C, {weather.desc}</span>
        </div>
      </div>

      {/* What is coming up, assembled from the school's own records.
          This replaced an embedded Google Calendar as the main card. The embed needed a school to be
          on Google Workspace and to keep a second calendar up to date by hand, so it showed whatever
          somebody had remembered to put in it -- never the activities, cover lessons and
          invigilation the platform already knew about. Nothing is synchronised here: the sources are
          read for the window on screen and merged. */}
      <div style={{
        flex: 1,
        backgroundColor: 'var(--color-bg-card)',
        borderRadius: isMobile ? 0 : 'var(--radius-lg)',
        overflow: 'hidden',
        boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
        display: 'flex', flexDirection: 'column'
      }}>
        <div style={{ padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' }}>
          <CalendarIcon size={20} color="var(--color-primary)" />
          {t('dashboard.comingUp')}
          <Link to="/dashboard/calendar-admin" style={{ marginLeft: 'auto', fontSize: '0.8rem', fontWeight: '500', color: 'var(--color-primary)', textDecoration: 'none' }}>
            {t('dashboard.fullCalendar')}
          </Link>
        </div>
        <div style={{ flex: 1, minHeight: 0, padding: isMobile ? '0.75rem' : '1rem 1.25rem', display: 'flex', flexDirection: 'column' }}>
          <SchoolAgenda schoolId={activeSchoolId} email={user?.email} isMobile={isMobile} compact />
        </div>
      </div>

      {/* Kept, and second. A school that configured an embed is still using it for whatever it holds
          that the platform does not, and removing it would take that away without asking. It no
          longer nags a school that never set one up: an empty card telling everyone to go and
          configure a Google Calendar is not advice this platform still wants to give. */}
      {calendarUrl && !hideEmbed && (
        <div style={{
          backgroundColor: 'var(--color-bg-card)',
          borderRadius: isMobile ? 0 : 'var(--radius-lg)',
          overflow: 'hidden',
          boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
          display: 'flex', flexDirection: 'column'
        }}>
          <div style={{ padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' }}>
            <CalendarIcon size={20} color="var(--color-text-muted)" />
            {t('dashboard.embeddedCalendar')}
            <Link to="/dashboard/system-settings" title={t('dashboard.configure')} style={{ marginLeft: 'auto', display: 'flex', color: 'var(--color-text-muted)' }}>
              <Settings size={16} />
            </Link>
          </div>
          <iframe
            title={t('dashboard.embeddedCalendarTitle')}
            src={calendarUrl}
            style={{ border: 0, width: '100%', height: '420px' }}
          />
        </div>
      )}

    </div>
  );
};

export default DashboardHome;
